<template>
    <!-- Loading spinner while theme loads -->
    <div v-if="shouldShowLoader" class="theme-loader">
        <v-progress-circular
            indeterminate
            color="#6b7280"
            :size="70"
            :width="7"
        ></v-progress-circular>
        <p class="loading-text">Loading...</p>
    </div>

    <!-- Main content (only show after theme loaded) -->
    <div v-else>
        <div v-if="!isIframe" id="header">
            <img v-if="companyLogo" :src="companyLogo" :alt="`${companyName} Logo`" class="logo-sticky scale-with-grid" height="105">
        </div>
        <div :id="isIframe ? 'iframe-container' : 'registration-container'" class="registration-form h-100 d-flex flex-column fill-height justify-center">
        <div class="content-container">
            <v-card ref="form" :class="isIframe ? '' : 'pa-6'">
                <div id="progressbar">
                    <div class="progress-step" :class="{ 'active': true, 'current': !expandCustomer && !expandAppointment && !expandBilling && !expandContract }">
                        <div class="step-content">
                            <div class="step-icon">
                                <v-icon size="24" color="primary" icon="mdi-map-marker-radius"></v-icon>
                            </div>
                            <span class="step-label">Address</span>
                        </div>
                        <div class="step-connector"></div>
                    </div>
                    <div class="progress-step" :class="{ 'active': expandCustomer || expandAppointment || expandBilling || expandContract, 'current': (expandCustomer || expandAppointment) && !expandBilling && !expandContract }">
                        <div class="step-content">
                            <div class="step-icon">
                                <v-icon size="24" color="primary" icon="mdi-account"></v-icon>
                            </div>
                            <span class="step-label">Account</span>
                        </div>
                        <div class="step-connector"></div>
                    </div>
                    <div class="progress-step" :class="{ 'active': expandBilling || expandContract, 'current': expandBilling || expandContract }">
                        <div class="step-content">
                            <div class="step-icon">
                                <v-icon size="24" color="primary" icon="mdi-routes-clock"></v-icon>
                            </div>
                            <span class="step-label">Schedule</span>
                        </div>
                    </div>
                </div>

                <!-- Display the address if available and show "Clear Address" button -->
                <div v-if="place && !billing">
                    <b>Your address:</b> {{ place.formatted_address }}
                    <b class="clickable text-brand-primary" @click="resetAddress">(Change)</b>
                    <!--<p>{{ place.geometry.location.lat }} - {{ place.geometry.location.lng }}</p>-->
                </div>

                <GetAddress v-if="!place" :company-slug="currentSlug" @send-place="handlePlace"/>

                <v-expand-transition>
                    <CreateCustomer @send-customer="handleCustomer" v-if="expandCustomer" :receivedPlace="place" :companyConfig="companyConfig"/>
                </v-expand-transition>
                <v-expand-transition>
                    <ScheduleAppointment @send-appointment="handleAppointment" v-show="expandAppointment" :receivedCustomer="customer" :receivedRoutes="routes" :receivedPlace="place"/>
                </v-expand-transition>
                <v-expand-transition>
                    <SetupBilling @send-billing="handleBilling" v-if="expandBilling" :receivedCustomer="customer" :receivedApppointment="appointment"/>
                </v-expand-transition>
                <v-expand-transition>
                    <SignContract @send-contract="handleContract" v-if="expandContract" :receivedBilling="billing" :receivedApppointment="appointment"/>
                </v-expand-transition>

                <div v-if="billing" class="text-center">
                    <a class="clickable text-brand-primary" @click="resetAddress">(have that "more than one house" problem? click here to create another account)</a>
                </div>
            </v-card>
        </div>

        <!--<p v-if="place">{{ place }}</p>-->

        <!--<img v-if="place"
      :data-src="google_map_url"
      alt="Google map live image."
      :src="google_map_url">-->

        <!-- Debug Panel -->
        <DebugPanel :companySlug="currentSlug" />
        </div>
    </div>
</template>

<script>
import GetAddress from '@/components/GetAddress.vue';
import CreateCustomer from '@/components/CreateCustomer.vue';
import ScheduleAppointment from '@/components/ScheduleAppointment.vue';
import SetupBilling from '@/components/SetupBilling.vue';
import SignContract from '@/components/SignContract.vue';
import DebugPanel from '@/components/DebugPanel.vue';

import axios from 'axios';
import analytics from '@/services/analytics';
import { loadCompanyConfig, companyConfig, configLoading } from '@/services/companyConfig';
import { initializeTheme } from '@/services/theme';

export default {
    name: 'RegistrationForm',
    components: {
        GetAddress,
        CreateCustomer,
        ScheduleAppointment,
        SetupBilling,
        SignContract,
        DebugPanel
    },
    data() {
        // Get company slug from URL path once (single source of truth)
        const pathSegments = window.location.pathname.split('/').filter(Boolean);
        const currentSlug = pathSegments[0] || 'mission';
        const getKey = (key) => `${currentSlug}-${key}`;

        const storedPlace = JSON.parse(localStorage.getItem(getKey('place'))) || null;
        const storedCustomer = JSON.parse(localStorage.getItem(getKey('customer'))) || null;
        const storedAppointment = JSON.parse(localStorage.getItem(getKey('appointment'))) || null;
        const storedBilling = JSON.parse(localStorage.getItem(getKey('billing'))) || null;
        return {
            currentSlug: currentSlug,  // Store slug as data property
            place: storedPlace || null,
            customer: storedCustomer || null,
            appointment: storedAppointment || null,
            billing: storedBilling || null,
            routes: null,
            expandCustomer: false,
            expandAppointment: false,
            expandBilling: false,
            expandContract: false,
            isIframe: false,
            apiBaseUrl: process.env.NODE_ENV === 'development'
            ? 'http://localhost:3000/api'  // Local server
            : 'https://api.getpestcontrol.services/api'  // Production server
        }
    },
    computed: {
        companyConfig() {
            return companyConfig.value;
        },
        configLoading() {
            return configLoading.value;
        },
        shouldShowLoader() {
            // Show loader while config is loading (for any slug)
            return this.configLoading;
        },
        companyName() {
            return this.companyConfig?.companyName || 'Mission Pest Control';
        },
        companyLogo() {
            // Priority 1: Check if Clerk organization has a logo uploaded
            if (this.companyConfig?.organizationLogoUrl) {
                return this.companyConfig.organizationLogoUrl;
            }

            // Priority 2: Check theme.logo from publicMetadata
            const logoPath = this.companyConfig?.theme?.logo;

            // If no logo path configured, use default Mission logo
            if (!logoPath) {
                return require('@/assets/mission/mission-logo.svg');
            }

            // If it's a URL (http or https), use it directly
            if (logoPath.startsWith('http://') || logoPath.startsWith('https://')) {
                return logoPath;
            }

            // For Mission's default logo path, use the static require
            if (logoPath === 'assets/mission/mission-logo.svg' ||
                logoPath === '/assets/mission/mission-logo.svg' ||
                logoPath === '@/assets/mission/mission-logo.svg') {
                return require('@/assets/mission/mission-logo.svg');
            }

            // For any other path, assume it's a public URL or return as-is
            // This will work if the logo is in the public folder
            return logoPath.startsWith('/') ? logoPath : `/${logoPath}`;
        },
        officeIDs() {
            return this.companyConfig?.integration?.officeIDs || '714';
        },
        google_map_url() {
            return 'https://maps.googleapis.com/maps/api/staticmap?size=325x256&amp;zoom=19&amp;scale=1&amp;style=feature:poi%7Cvisibility:off&amp;format=png&amp;maptype=satellite&amp;center='
                + this.place.geometry.location.lat + ',' + this.place.geometry.location.lng + '&amp;key=AIzaSyA77NAZeswyMcHHhnHqwh04GQifH8Xe2aM'
        }
    },
    async mounted() {
    // Load company configuration using our single source of truth
    await loadCompanyConfig(this.currentSlug);

    // Apply theme after config is loaded and DOM is ready
    // Use $nextTick to ensure Vuetify is fully initialized
    await this.$nextTick();
    initializeTheme(this.$vuetify);

    // Detect if `iframe=true` is in the query parameter
    const queryParams = new URLSearchParams(window.location.search);
    this.isIframe = queryParams.get('iframe') === 'true';

    // Track registration start
    analytics.trackRegistrationStart();

    if (this.place && Object.keys(this.place).length > 0) {
        this.handlePlace(this.place);
    }

    if (this.customer && Object.keys(this.customer).length > 0) {
        this.handleCustomer(this.customer);
    }

    if (this.appointment && Object.keys(this.appointment).length > 0) {
        this.handleAppointment(this.appointment);
    }

    if (this.billing && Object.keys(this.billing).length > 0) {
        this.handleBilling(this.billing);
    }
    },
    methods: {
        getStorageKey(key) {
            return `${this.currentSlug}-${key}`;
        },
        handlePlace(value) {
            // Handles the custom event from GetAddress and updates the data
            this.place = value;
            this.expandCustomer = true;

            // Track address selection step (no personal data)
            analytics.trackFormStep('address_selection');
        },
        handleCustomer(value) {
            // Handles the new customer and updates the data
            this.customer = value;
            this.expandCustomer = false;
            this.expandAppointment = true;
            this.getRoutes(this.place);
            
            // Track customer creation step (no personal data)
            analytics.trackFormStep('customer_creation', {
                pestCount: value.pests ? value.pests.length : 0
            });
        },
        handleAppointment(value) {
            // Handles apppointment and updates the data
            this.appointment = value;
            this.expandAppointment = false;
            this.expandBilling = true;
            
            // Track appointment scheduling step (no personal data)
            analytics.trackFormStep('appointment_scheduling');
        },
        handleBilling(value) {
            // Handles billing and updates the data
            this.billing = value;
            this.expandBilling = false;
            this.expandContract = true;
            
            // Track billing setup step and treat as sale completion (no personal data)
            analytics.trackFormStep('billing_setup', {
                planType: value.planType,
                frequency: value.frequency
            });
            
            // Track registration completion since billing is the last trackable step
            analytics.trackRegistrationComplete({
                planType: value.planType,
                frequency: value.frequency
            });
        },
        handleContract() {
            // Handle contract completion (external website handles this step)
            // No analytics tracking here since this happens on external site
        },
        resetAddress() {
            // Clear data and reset steps
            this.place = null;
            this.customer = null;
            this.appointment = null;
            this.routes = null;
            this.billing = null;
            this.expandCustomer = false;
            this.expandAppointment = false;
            this.expandBilling = false;
            this.expandContract = false;

            // Clear data from local storage using dynamic keys
            const placeKey = this.getStorageKey('place');
            const customerKey = this.getStorageKey('customer');
            const routesKey = this.getStorageKey('routes');
            const appointmentKey = this.getStorageKey('appointment');
            const billingKey = this.getStorageKey('billing');

            localStorage.removeItem(placeKey);
            localStorage.removeItem(customerKey);
            localStorage.removeItem(routesKey);
            localStorage.removeItem(appointmentKey);
            localStorage.removeItem(billingKey);
        },
        async getRoutes(place) {

            if(!place) {
                return;
            }

            // Load routes from storage using dynamic key // TODO: do we need this after dev? trying to save api hits..
            const routesCache = JSON.parse(localStorage.getItem(this.getStorageKey('routes')));
            if(routesCache) {
                if (routesCache.length > 0) {
                    //this.expandAppointment = !this.expandAppointment;
                    return;
                }
            }

            // TODO: dateStart and dateEnd should be shared with ScheduleAppointment so users can override them?
            const dateStart = new Date(new Date());
            dateStart.setDate(dateStart.getDate() + 2);

            const dateEnd = new Date(dateStart);
            dateEnd.setDate(dateEnd.getDate() + 14);

            const routeSearchDetails = {
                officeIDs: this.officeIDs, // Use dynamic officeIDs from config
                latitude: place.geometry.location.lat,
                longitude: place.geometry.location.lng,
                maxDistance: 7,
                dateStart: dateStart.toISOString().split('T')[0], // format as 'YYYY-MM-DD'
                dateEnd: dateEnd.toISOString().split('T')[0], // format as 'YYYY-MM-DD'
            }

            const searchResponse = await axios.post(`${this.apiBaseUrl}/route/get`, routeSearchDetails);
            this.routes = searchResponse.data.routes;

            // persist routes to local storage using dynamic key
            localStorage.setItem(this.getStorageKey('routes'), JSON.stringify(this.routes));
        }
    },
    watch: {
        // Watch for changes and persist to localStorage with dynamic keys
        place(newValue) {
            if (newValue) {
                localStorage.setItem(this.getStorageKey('place'), JSON.stringify(newValue));
            } else {
                localStorage.removeItem(this.getStorageKey('place'));
            }
        },
        customer(newValue) {
            if (newValue) {
                localStorage.setItem(this.getStorageKey('customer'), JSON.stringify(newValue));
            } else {
                localStorage.removeItem(this.getStorageKey('customer'));
            }
        },
        appointment(newValue) {
            if (newValue) {
                localStorage.setItem(this.getStorageKey('appointment'), JSON.stringify(newValue));
            } else {
                localStorage.removeItem(this.getStorageKey('appointment'));
            }
        },
        billing(newValue) {
            if (newValue) {
                localStorage.setItem(this.getStorageKey('billing'), JSON.stringify(newValue));
            } else {
                localStorage.removeItem(this.getStorageKey('billing'));
            }
        }
    }
}
</script>

<style>
/* Theme Loading Spinner */
.theme-loader {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    gap: 20px;
    background: #f5f5f5;
}

.theme-loader .loading-text {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 1.2em;
    color: #6b7280;
    margin: 0;
}

h1 {
    color: var(--brand-primary);
    font-family: var(--font-heading);
}

#header {
    text-align: center;
    background-color: var(--header-bg);
}

#header img {
    height: var(--logo-height);
    padding: 0.5em;
}

#registration-container.registration-form {
    position: relative;
    min-height: calc(100vh - var(--header-height));
    background: linear-gradient(135deg, var(--color-light) 0%, var(--color-surface) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
}

.content-container {
    width: 100%;
    max-width: 900px;
    background-color: var(--card-bg);
    padding: var(--card-padding);
    border-radius: var(--card-border-radius);
}

form {
    max-width: 600px;
}

/*iframe*/
#iframe-container {
  background: none; /* Remove default background */
  margin: 0;
  padding: 0;
  border: none;
  height: 100vh; /* Ensure full viewport height */
  width: 100vw; /* Ensure full viewport width */
  display: flex;
  align-items: center; /* Center content vertically */
  justify-content: center; /* Center content horizontally */
  overflow: hidden; /* Prevent unnecessary scrollbars */
}

#iframe-container .v-card {
    box-shadow: none;
}

/*progressbar*/
#progressbar {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0;
    margin-bottom: 20px;
    padding: 0;
}

.progress-step {
    display: flex;
    align-items: center;
    position: relative;
}

.step-content {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    border-radius: 24px;
    background: var(--color-inactive-bg);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    z-index: 2;
}

.progress-step.active .step-content {
    background: var(--brand-primary);
    box-shadow: 0 2px 8px var(--color-shadow);
}

.step-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: transparent;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.progress-step.active .step-icon {
    background: white;
}

.step-label {
    font-size: 14px;
    font-weight: 600;
    color: var(--color-inactive-text);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    white-space: nowrap;
}

.progress-step.active .step-label {
    color: white;
}

.step-connector {
    width: 40px;
    height: 2px;
    background: var(--color-inactive-border);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    z-index: 1;
}

.progress-step.active .step-connector {
    background: var(--brand-primary);
}

.progress-step:last-child .step-connector {
    display: none;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
    .step-label {
        display: none;
    }

    .progress-step.current .step-label {
        display: block;
    }

    .step-content {
        padding: 8px;
    }

    .progress-step.current .step-content {
        padding: 8px 16px;
    }

    .step-connector {
        width: 20px;
    }
}

/* Clickable elements cursor styling */
a, b {
    cursor: pointer;
    user-select: none;
}

.clickable {
    cursor: pointer;
    user-select: none;
    transition: var(--button-transition);
}

.clickable:hover {
    opacity: 0.85;
}
</style>
